/**
 * Weather system. Time is divided into fixed BLOCKS (10 min each — a quarter of the
 * 40-minute day, so four per day); each block's weather TYPE and its WIND SPEED
 * (mph) are deterministically derived from the block index, so the sequence is
 * consistent across reloads/devices and a short FORECAST is just the next few blocks. Visuals (clouds/rain/sway) read the active
 * params here; wind is expressed as a real mph value the HUD can show.
 *
 * Wind is COUPLED to the weather type so it "makes sense": each type declares an
 * allowable [windMin, windMax] mph band, and the per-block wind is sampled within
 * it. Calm/sunny types sit low; storms sit high; cloudy types span a wide range.
 * The visual strength used by the renderers is derived from mph in one place
 * (windStrengthFromMph) so sway + cloud drift stay consistent everywhere.
 */

export type WeatherType =
  | "clear"         // clear skies — calm, a wisp or two; the calm end of the axis
  | "fog"           // still, damp fog — dead calm, the whole scene washed pale
  | "partlyCloudy"  // partly cloudy — light/variable breeze
  | "mostlyCloudy"  // mostly cloudy — moderate, variable wind
  | "overcast"      // grey overcast — moderate wind
  | "lightRain"     // light rain — gentle wind, sparse drops
  | "heavyRain"     // heavy rain — strong wind, dense drops
  | "thunderstorm"; // thunderstorm — violent wind, torrential rain

/**
 * Weather changes only on a CHUNK boundary. A "day" is 40 real minutes (matches
 * dayNight.DAY_MS), split into 4 chunks of 10 min — one per time-of-day quarter
 * (morning / afternoon / evening / night). Weather holds steady within a chunk and
 * may persist across many chunks (realistic — it can drizzle for a chunk or storm
 * across several), shifting only between plausible neighbours via a Markov chain.
 */
export const DAY_MS = 40 * 60 * 1000;            // THE game day — render/dayNight re-exports this one
export const CHUNK_MS = DAY_MS / 4;               // 10 min per chunk
export const CHUNKS_PER_DAY = 4;
/** Back-compat alias (older callers referenced a 4-min "block"). */
export const WEATHER_BLOCK_MS = CHUNK_MS;

/** Cloud silhouette style per weather. */
export type CloudForm = "wispy" | "puffy" | "cumulus";

/** Visual + physical parameters each weather type drives. */
export interface WeatherParams {
  type: WeatherType;
  label: string;
  /** Cloud silhouette style (long wispy / rounded puffy / massive towering). */
  cloudForm: CloudForm;
  /** Rough number of clouds drifting across (spread over parallax layers). */
  cloudCount: number;
  /** Cloud opacity 0..1 and a darkness factor (0 = white, 1 = storm-grey). */
  cloudOpacity: number;
  cloudDark: number;
  /** Rain intensity 0..1 (0 = none). */
  rain: number;
  /** Lightning flashes (thunderstorm only). */
  lightning: boolean;
  /** How far the SKY GRADIENT is pulled toward this weather's overcast colour
   *  (0 = pristine day/night sky, 1 = fully the storm grey). */
  skyTint: number;
  /** The grey/overcast colour the sky blends toward (0xRRGGBB), weighted by skyTint. */
  skyTintColor: number;
  /** Scene gloom: a cool dark veil (0..1) drawn over the whole RENDERED scene (sky, plants, racks, grass)
   *  — but NOT the UI — so dim weather darkens everything.
   *
   *  THIS COLUMN MUST RISE MONOTONICALLY down the table, in the same order as `cloudDark`: clear → fog →
   *  partlyCloudy → mostlyCloudy → overcast → lightRain → heavyRain → thunderstorm. It is a FLOOR under
   *  the day/night multiply, not an independent instrument — the renderers take
   *  `max(sky.nightMultiplyK, sceneDark * 0.7)` — so a value that steps backwards here can invert the
   *  weather ramp on screen even when `dayNight.ts` has it right. Fog is the one that got this wrong: it
   *  sat at 0.1, above partly cloudy's 0.08. Fog washes out; it does not darken. */
  sceneDark: number;
  /** Allowable wind band for this type, in mph (the per-block speed is sampled
   *  within this range; the dev tool clamps its slider to it). */
  windMin: number;
  windMax: number;
}

const PARAMS: Record<WeatherType, Omit<WeatherParams, "type">> = {
  // Clear skies: the pristine DEFAULT sky and the CALM END of the severity axis — no tint, no gloom.
  // It absorbed the old "bright" state, which claimed harsh SUN and so read as nonsense at night; a clear
  // sky is true at any hour, which is the whole reason the axis ends here. Wind band widened to 0-8 to
  // cover the dead-calm end bright used to hold.
  clear:        { label: "Clear Skies",    cloudForm: "wispy",   cloudCount: 3,  cloudOpacity: 0.8,  cloudDark: 0,    rain: 0,    lightning: false, skyTint: 0,    skyTintColor: 0x9fc4dc, sceneDark: 0, windMin: 0,  windMax: 8 },
  // Fog: the odd one out on the calm-to-stormy axis. It is not a degree of cloud — it is cloud AT GROUND
  // LEVEL, so it is the only type that is simultaneously DEAD CALM (fog cannot form in wind; it is the
  // narrowest band here, 0-3mph) and heavily veiled. The tint is pushed hard (0.72) toward a PALE, slightly
  // warm grey rather than the storm greys, and `sceneDark` is the LOWEST of any veiled state (0.04): fog
  // does not darken a scene, it washes it out and eats contrast. It was 0.1, which put fog ABOVE partly
  // cloudy (0.08) on the gloom column — the thinner, calmer condition rendering DARKER than the cloudier
  // one, the same inversion the lighting model had at the clear end. The wash lives in `skyTint` (0.72,
  // pale grey) where it belongs; this column only ever darkens, so fog takes barely any of it. Cloud count
  // is high with low opacity so the deck reads as a formless bank rather than discrete puffs.
  fog:          { label: "Fog",            cloudForm: "wispy",   cloudCount: 14, cloudOpacity: 0.55, cloudDark: 0.05, rain: 0,    lightning: false, skyTint: 0.72, skyTintColor: 0xd3d8d8, sceneDark: 0.04,  windMin: 0,  windMax: 3 },
  // Partly cloudy: a clear grey haze (darker than before).
  partlyCloudy: { label: "Partly Cloudy",  cloudForm: "puffy",   cloudCount: 6,  cloudOpacity: 0.9,  cloudDark: 0.1,  rain: 0,    lightning: false, skyTint: 0.34, skyTintColor: 0x97a7b2, sceneDark: 0.08, windMin: 3,  windMax: 16 },
  // Mostly cloudy: distinctly grey, flatter sky.
  mostlyCloudy: { label: "Mostly Cloudy",  cloudForm: "puffy",   cloudCount: 10, cloudOpacity: 0.95, cloudDark: 0.22, rain: 0,    lightning: false, skyTint: 0.6,  skyTintColor: 0x808d97, sceneDark: 0.16, windMin: 5,  windMax: 26 },
  // Overcast: dull, dark-grey deck.
  overcast:     { label: "Overcast",       cloudForm: "cumulus", cloudCount: 13, cloudOpacity: 0.97, cloudDark: 0.42, rain: 0,    lightning: false, skyTint: 0.8,  skyTintColor: 0x6b757f, sceneDark: 0.24, windMin: 6,  windMax: 24 },
  // Light rain: dark grey, a touch cooler/bluer than overcast.
  lightRain:    { label: "Light Rain",     cloudForm: "cumulus", cloudCount: 12, cloudOpacity: 0.97, cloudDark: 0.5,  rain: 0.35, lightning: false, skyTint: 0.86, skyTintColor: 0x5c6671, sceneDark: 0.3,  windMin: 4,  windMax: 18 },
  // Heavy rain: very dark, leaden grey.
  heavyRain:    { label: "Heavy Rain",     cloudForm: "cumulus", cloudCount: 14, cloudOpacity: 1,    cloudDark: 0.66, rain: 0.8,  lightning: false, skyTint: 0.93, skyTintColor: 0x434b56, sceneDark: 0.4,  windMin: 18, windMax: 42 },
  // Thunderstorm: near-black charcoal, deeply gloomy scene.
  thunderstorm: { label: "Thunderstorm",   cloudForm: "cumulus", cloudCount: 16, cloudOpacity: 1,    cloudDark: 0.8,  rain: 1,    lightning: true,  skyTint: 0.98, skyTintColor: 0x2b313b, sceneDark: 0.5,  windMin: 30, windMax: 60 },
};

/**
 * THE ONLY THREE RAIN THRESHOLDS THERE ARE. `rainMin` looks like a continuum and is not: the weather chain
 * emits exactly four rain values (`PARAMS` above) — 0 for every dry sky, 0.35 (lightRain), 0.8 (heavyRain)
 * and 1 (thunderstorm) — so only three distinct gates exist, and every value in between is an alias for the
 * one at or below it. Write one of these three and nothing else, in the fish roster (fishConds.ts, held to it
 * by `scripts/smoke.mjs`) and the insect roster (insects.ts, held to it by src/sim/insectRoster.test.ts).
 * CONDITIONS.md §Weather repeats the table.
 *
 * Any other number is a LIE about how selective the gate is, and the rosters had been reading those lies
 * back as design intent. 0.2 and 0.3 both meant RAIN_ANY while their comments distinguished "drizzle" from
 * "any real rain"; 0.4, 0.5 and 0.8 all meant RAIN_HEAVY. The Rapidsprite is what that cost: a COMMON at
 * weight 93 written as 0.4 and commented "real rain, not the 0.2 drizzle", which in fact locked it to
 * heavy-rain-or-storm, about 4% of chunks — a rarer window than any legendary's night gate, on a fish
 * meant to be one of the first things a new garden catches out of a stream.
 */
/** Any rain at all — light, heavy or storm. The damp-lover's gate, and the one a common may spend. */
export const RAIN_ANY = 0.35;
/** A downpour: heavy rain or a thunderstorm. Roughly a twentieth of the calendar — treat as a rare tier's gate. */
export const RAIN_HEAVY = 0.8;
/** Thunderstorm only. Prefer `weather: ["thunderstorm"]`, which says the same thing and reads as the sky it is. */
export const RAIN_STORM = 1;

/** Every weather type, in display order (for the dev picker). */
export const WEATHER_TYPES: WeatherType[] = [
  "clear", "fog", "partlyCloudy", "mostlyCloudy", "overcast", "lightRain", "heavyRain", "thunderstorm",
];

/**
 * Coerce an UNTRUSTED weather string to a real type, falling back to `clear`.
 *
 * Weather names leak into places we do not control: an automation rule inside a save, a `?weather=` URL on
 * the shot harness, a DB tuning row. "bright" was a real type until it was removed (it could report harsh
 * sun at midnight) and any of those may still name it — as may a typo or a future removal. Everything
 * unknown lands on `clear`, the calm end of the axis that absorbed bright, so a stale value renders a
 * plausible sky instead of throwing or leaving `paramsFor` to return undefined and draw nothing.
 */
export function coerceWeatherType(raw: string | null | undefined): WeatherType {
  return raw != null && Object.prototype.hasOwnProperty.call(PARAMS, raw) ? (raw as WeatherType) : "clear";
}

/** Whole-CHUNK index for a timestamp (10-min slices, four per day; weather changes on these). */
export function weatherChunk(now: number): number {
  return Math.floor(now / CHUNK_MS);
}
/** Back-compat: older callers say "block" — same thing now (one 10-min chunk). */
export function weatherBlock(now: number): number { return weatherChunk(now); }

/** ms until the next chunk boundary (the next possible weather change). */
export function msUntilNextChunk(now: number): number {
  return CHUNK_MS - (now % CHUNK_MS);
}
export function msUntilNextBlock(now: number): number { return msUntilNextChunk(now); }

function hash01(n: number): number {
  let h = Math.imul(n ^ 0x9e3779b9, 2654435761);
  h ^= h >>> 15;
  h = Math.imul(h, 2246822519);
  h ^= h >>> 13;
  return (h >>> 0) / 4294967296;
}

// ---- REALISTIC WEATHER CHAIN ------------------------------------------------
// Weather follows plausible day-to-day patterns: it tends to PERSIST, and when it
// shifts it moves to a NEIGHBOURING condition on a calm↔stormy spectrum (never
// e.g. bright → thunderstorm in one step). Each chunk's weather is a deterministic
// Markov walk from a fixed anchor, so it's identical across reloads/devices and
// the forecast is just "keep walking". Persistence (self-transition prob) is high,
// so a condition can last one chunk or many — random but realistic.

// Per-state transition weights to every type (rows sum need not be 1; normalised).
// High diagonal = persistence; mass concentrated on adjacent severities.
// FOG sits OFF the severity axis, which is why its row and column look unlike the others. It is not a
// step between clear and cloudy: it forms in still, damp, settled air, so it is entered ONLY from the calm
// half (clear / partlyCloudy) and from overcast — never from rain. It also BURNS OFF rather than deepening:
// its own row sends most of its mass back to clear and partlyCloudy, and none at all past overcast. Fog
// therefore stays a genuinely uncommon, short-lived event: it holds ~2.4% of chunks in spells averaging
// ~1.5 chunks (~15 real minutes). That is what makes it worth gating a fish on — rare enough to hunt for,
// common enough to actually meet.
//
// CLEAR is the calm end of the axis. It used to share that end with a "bright" state that claimed harsh
// sun, which the HUD could report at midnight — so bright was removed and clear ABSORBED it, row and
// column. The clear row is not the old clear row: it is the steady-state-weighted BLEND of the old bright
// and clear rows with the bright column folded in, which is what keeps the lumped state behaving like the
// pair it replaces. Every other state's long-run share is within 0.2pp of where it sat before the removal
// (partlyCloudy 21.93 -> 21.93, overcast 11.91 -> 11.78, thunderstorm 1.10 -> 1.09), and clear now holds
// 34.25% where bright+clear together held 33.89%. Do not "tidy" the clear row toward the others' shape:
// its high 74 diagonal is the merged persistence of two states, not a typo.
const ORDER: WeatherType[] = ["clear", "fog", "partlyCloudy", "mostlyCloudy", "overcast", "lightRain", "heavyRain", "thunderstorm"];
//                              clr  fog  pc   mc   ovc  lr   hr   ts   (every row sums to 100)
const TRANSITIONS: Record<WeatherType, number[]> = {
  clear:        [ 74,   2,  18,   5,   1,   0,   0,   0],
  fog:          [ 32,  34,  22,   8,   4,   0,   0,   0],
  partlyCloudy: [ 29,   3,  42,  18,   6,   2,   0,   0],
  mostlyCloudy: [  8,   0,  24,  40,  18,   8,   2,   0],
  overcast:     [  3,   2,  12,  22,  36,  18,   6,   1],
  lightRain:    [  1,   0,   6,  16,  24,  38,  12,   3],
  heavyRain:    [  0,   0,   2,   8,  18,  26,  34,  12],
  thunderstorm: [  0,   0,   1,   5,  14,  26,  26,  28],
};

/** Pick the next state from `from` using a 0..1 roll against its transition row. */
function nextState(from: WeatherType, roll: number): WeatherType {
  const row = TRANSITIONS[from];
  let total = 0;
  for (const w of row) total += w;
  let acc = 0;
  const target = roll * total;
  for (let i = 0; i < ORDER.length; i++) {
    acc += row[i]!;
    if (target < acc) return ORDER[i]!;
  }
  return from;
}

// Deterministic walk, memoised. Anchored to a fixed epoch chunk so any query walks
// the SAME sequence; we cache the furthest-computed chunk so repeated/forward
// queries are cheap.
const ANCHOR_STATE: WeatherType = "clear";
const chainCache: WeatherType[] = [ANCHOR_STATE]; // chainCache[i] = weather at chunk i (i >= 0)

function chunkWeather(chunk: number): WeatherType {
  const c = Math.max(0, Math.floor(chunk));
  for (let i = chainCache.length; i <= c; i++) {
    const roll = hash01(i * 2654435761 + 12345);
    chainCache[i] = nextState(chainCache[i - 1]!, roll);
  }
  return chainCache[c]!;
}

/** Weather type at a given chunk index (deterministic, realistic chain). */
export function weatherForBlock(chunk: number): WeatherType { return chunkWeather(chunk); }

/** Deterministic wind speed (mph) for a chunk, sampled within its type's band.
 *  Skewed slightly toward the calmer end so peak winds feel like an event. */
export function windMphForBlock(chunk: number, type: WeatherType): number {
  const p = PARAMS[type];
  const u = hash01(chunk * 40503 + 7); // 0..1
  const skew = u * u * 0.5 + u * 0.5;  // gentle low-end bias
  return Math.round(p.windMin + (p.windMax - p.windMin) * skew);
}

export function paramsFor(type: WeatherType): WeatherParams {
  return { type, ...PARAMS[type] };
}

/** The active weather params at a timestamp. */
export function weatherAt(now: number): WeatherParams {
  return paramsFor(weatherForBlock(weatherBlock(now)));
}

/** The active wind speed (mph) at a timestamp, for the current block + type. */
export function windMphAt(now: number, type?: WeatherType): number {
  const block = weatherBlock(now);
  return windMphForBlock(block, type ?? weatherForBlock(block));
}

/**
 * Map a wind speed (mph) to the renderer's unitless sway/drift STRENGTH. One place
 * so plants, grass, trees, and cloud drift all agree. Tuned to stay gentle — even
 * a gale should sway, not snap:
 *   0–5 mph   barely moves     → ~0.05–0.15
 *   ~12 mph   gentle sway      → ~0.35
 *   ~30 mph   noticeable lean  → ~0.7
 *   60 mph    strong but sane  → ~1.1
 * A compressive curve (sub-linear, soft-capped) so high winds don't explode.
 */
export function windStrengthFromMph(mph: number): number {
  const m = Math.max(0, mph);
  return 0.05 + Math.pow(m / 55, 0.8) * 1.05;
}

/** A short forecast: the next `n` chunks' types (index 0 = current). */
export function forecast(now: number, n = 4): WeatherType[] {
  const b = weatherChunk(now);
  return Array.from({ length: n }, (_, i) => weatherForBlock(b + i));
}

// ---- RICH FORECAST (for the modal) -----------------------------------------

/** The four chunks of a day, in order, with a label + representative in-game clock. */
export const CHUNK_LABELS = ["Morning", "Afternoon", "Evening", "Night"] as const;
const CHUNK_CLOCK = ["6:00 AM", "12:00 PM", "6:00 PM", "12:00 AM"] as const;

export interface ForecastChunk {
  chunk: number;          // absolute chunk index
  dayOffset: number;      // 0 = today … 6
  chunkOfDay: number;     // 0..3
  partLabel: string;      // "Morning" | "Afternoon" | "Evening" | "Night"
  clock: string;          // representative in-game clock for that chunk
  type: WeatherType;
  params: WeatherParams;
  windMph: number;
  isNow: boolean;         // the currently-active chunk
}

export interface ForecastDay {
  dayOffset: number;      // 0 = today
  chunks: ForecastChunk[];
}

/**
 * A multi-day forecast grouped by day. `days` covers today + (days-1) ahead; each
 * day has its 4 chunks (morning/afternoon/evening/night) with type + wind + the
 * in-game clock for that part of day. Starts from the FIRST chunk of today so the
 * grid is aligned to day boundaries (past chunks of today are still shown, greyed
 * by the caller via isNow/relative position if desired).
 */
export function forecastDays(now: number, days = 7): ForecastDay[] {
  const nowChunk = weatherChunk(now);
  const todayStart = nowChunk - (((nowChunk % CHUNKS_PER_DAY) + CHUNKS_PER_DAY) % CHUNKS_PER_DAY);
  const out: ForecastDay[] = [];
  for (let d = 0; d < days; d++) {
    const chunks: ForecastChunk[] = [];
    for (let c = 0; c < CHUNKS_PER_DAY; c++) {
      const chunk = todayStart + d * CHUNKS_PER_DAY + c;
      const type = weatherForBlock(chunk);
      chunks.push({
        chunk, dayOffset: d, chunkOfDay: c,
        partLabel: CHUNK_LABELS[c]!, clock: CHUNK_CLOCK[c]!,
        type, params: paramsFor(type), windMph: windMphForBlock(chunk, type),
        isNow: chunk === nowChunk,
      });
    }
    out.push({ dayOffset: d, chunks });
  }
  return out;
}

// ---- LONG-RUN SHARES --------------------------------------------------------
/**
 * The share of all chunks each weather type holds in the long run — the stationary distribution of
 * TRANSITIONS above, power-iterated once at module load rather than typed in as constants, so it can
 * never drift from the chain it describes. (It reproduces the figures quoted in the TRANSITIONS header:
 * clear 34.25%, partlyCloudy 21.93%, overcast 11.78%, fog 2.39%, thunderstorm 1.09%.)
 *
 * This is what makes a weather GATE priceable. "How many types does this fish accept" says nothing about
 * how hard it is to meet — a fish taking clear OR partly cloudy sees a sky it can use 56% of the time,
 * while the lone thunderstorm fish waits for 1.09%. The fish value model (`sim/fishValueModel.ts`) reads
 * these to cost a weather clause by the share of the ALLOWED SET, which is the only reading under which
 * accepting more types makes a fish easier rather than, absurdly, rarer.
 */
export const WEATHER_SHARE: Record<WeatherType, number> = (() => {
  let p = ORDER.map(() => 1 / ORDER.length);
  // 2000 iterations is far past convergence for an 8-state chain this well mixed (it settles by ~200).
  for (let it = 0; it < 2000; it++) {
    const next = ORDER.map(() => 0);
    for (let i = 0; i < ORDER.length; i++) {
      const row = TRANSITIONS[ORDER[i]!]!;
      let tot = 0;
      for (const w of row) tot += w;
      for (let j = 0; j < ORDER.length; j++) next[j]! += p[i]! * (row[j]! / tot);
    }
    p = next;
  }
  const out = {} as Record<WeatherType, number>;
  ORDER.forEach((t, i) => { out[t] = p[i]!; });
  return out;
})();

/** The share of chunks matching ANY of `types` (a fish's `weather` clause) — the union of their shares. */
export function weatherShareOfSet(types: readonly WeatherType[]): number {
  let s = 0;
  for (const t of new Set(types)) s += WEATHER_SHARE[t] ?? 0;
  return s;
}

/**
 * The share of chunks satisfying a fish's weather clause AND its rain clause TOGETHER.
 *
 * Rain is also NOT a continuum: the chain only ever emits rain 0 (every dry type), 0.35 (light rain), 0.8
 * (heavy rain) and 1 (thunderstorm), so `rainMin` has only THREE real thresholds — any rain, heavy-or-storm,
 * storm — and any value in between is an alias for the one at or below it. Anything above 1 can never open.
 * The fish roster is held to the three by `scripts/smoke.mjs` and the insects by insectRoster.test.ts; write
 * `RAIN_ANY`/`RAIN_HEAVY`/`RAIN_STORM` (defined above) rather than a bare number. This function still takes any number, because an admin
 * override out of the live-tuning key can contain one and must be priced honestly when it does.
 *
 * Nor are the two clauses independent rolls — they read the same sky. Several fish carry both (the Voltfin wants
 * thunderstorm AND rain ≥ 0.5; thunderstorm already rains at 1, so the rain clause costs it nothing extra),
 * and pricing the two marginals separately invents scarcity that the calendar does not contain. Taking the
 * JOINT share charges each sky exactly once for how often it is actually available.
 */
export function skyGateShare(types: readonly WeatherType[] | undefined, rainMin: number | undefined): number {
  const allowed = types?.length ? new Set(types) : null;
  const needRain = rainMin ?? 0;
  let s = 0;
  for (const t of ORDER) {
    if (allowed && !allowed.has(t)) continue;
    if (needRain > 0 && PARAMS[t].rain < needRain) continue;
    s += WEATHER_SHARE[t];
  }
  return s;
}
